import React, { useState, useRef, useEffect } from 'react';
import './WheelEditor.css';

const PencilIcon = ({ active }) => (
  <svg
    width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke={active ? "#09F984" : "#38bdf8"}
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
  >
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

const rgbToHex = (rgb) => {
  const m = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!m) return null;
  return "#" + [m[1], m[2], m[3]]
    .map((n) => (+n).toString(16).padStart(2, "0"))
    .join("");
};

const isTransparent = (color) => {
  if (!color || color === "transparent") return true;
  const m = color.match(/rgba\([^)]+,\s*([\d.]+)\)/);
  if (m && parseFloat(m[1]) === 0) return true;
  return false;
};

const pickColorFromElement = (el) => {
  let cur = el;
  while (cur && cur !== document.documentElement) {
    const bg = getComputedStyle(cur).backgroundColor;
    if (!isTransparent(bg)) return rgbToHex(bg);
    cur = cur.parentElement;
  }
  return null;
};


const toHex6 = (value) => {
  if (!value) return '#000000';
  if (value.startsWith('#') && value.length >= 7) return value.slice(0, 7);
  return '#000000';
};

const ColorRow = ({ label, colorKey, value, isPickActive, onPencilClick, onColorChange }) => (
  <div className="color-input">
    <label>{label}</label>
    <button
      className={`color-edit-btn${isPickActive ? " active" : ""}`}
      onClick={() => onPencilClick(colorKey)}
      title={isPickActive ? "İptal (Esc)" : "Sayfadan renk seç"}
    >
      <PencilIcon active={isPickActive} />
    </button>
    <label className="color-swatch-container" style={{ cursor: 'pointer' }}>
      <div className="color-swatch" style={{ backgroundColor: value }} />
      <input
        type="color"
        value={toHex6(value)}
        onChange={(e) => onColorChange(colorKey, e.target.value)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
      />
    </label>
    <span className="color-value-text">{value}</span>
  </div>
);

const WheelEditor = ({ colors, onColorChange, onClose }) => {
  const [pickMode, setPickMode] = useState({ active: false, key: null });
  const editorRef = useRef(null);
  const onColorChangeRef = useRef(onColorChange);
  useEffect(() => { onColorChangeRef.current = onColorChange; }, [onColorChange]);

  const cancelPick = () => setPickMode({ active: false, key: null });

  // Normal renk alanları için
  const handlePencilClick = async (key) => {
    if (pickMode.active && pickMode.key === key) { cancelPick(); return; }

    if (typeof window.EyeDropper !== "undefined") {
      try {
        const { sRGBHex } = await new window.EyeDropper().open();
        onColorChangeRef.current(key, sRGBHex);
      } catch {}
      return;
    }
    setPickMode({ active: true, key });
  };


  useEffect(() => {
    if (!pickMode.active) return;

    document.body.classList.add("color-pick-mode");

    const pick = (e) => {
      if (editorRef.current?.contains(e.target)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const hex = pickColorFromElement(e.target);
      if (hex) onColorChangeRef.current(pickMode.key, hex);
      cancelPick();
    };

    const cancel = (e) => { if (e.key === "Escape") cancelPick(); };

    document.addEventListener("click", pick, true);
    document.addEventListener("keydown", cancel);

    return () => {
      document.body.classList.remove("color-pick-mode");
      document.removeEventListener("click", pick, true);
      document.removeEventListener("keydown", cancel);
    };
  }, [pickMode.active, pickMode.key]);

  const row = (label, key) => (
    <ColorRow
      key={key}
      label={label}
      colorKey={key}
      value={colors[key] || "#000000"}
      isPickActive={pickMode.active && pickMode.key === key}
      onPencilClick={handlePencilClick}
      onColorChange={onColorChange}
    />
  );

  return (
    <div ref={editorRef} className="css-editor wheel-editor-panel">
      <div className="editor-header">
        <h3>Çark Editörü</h3>
        <button className="close-editor" onClick={onClose}>×</button>
      </div>

      {pickMode.active && (
        <div className="pick-hint">
          <span>Sayfada bir renge tıkla</span>
          <kbd>Esc</kbd>
          <span>iptal</span>
        </div>
      )}

      <div className="editor-section">
        <h4>Çark Renkleri</h4>
        {row("Koyu Arka Plan:", "backgroundColor1")}
        {row("Açık Arka Plan:", "backgroundColor2")}
      </div>

      <div className="editor-section">
        <h4>Yazı Renkleri</h4>
        {row("Ana Yazı:", "textColor1")}
        {row("İkincil Yazı:", "textColor2")}
      </div>

      <div className="editor-section">
        <h4>Diğer Renkler</h4>
        {row("Kenar Rengi:", "borderColor")}
        {row("İşaretçi Rengi:", "pointerColor")}
        {row("Çizgi Rengi:", "radiusLineColor")}
      </div>

      <div className="editor-section">
        <h4>Parıltı Efekti</h4>
        {row("Parıltı Rengi:", "wheelGlowColor")}
        <div className="color-input">
          <label>Parıltı Opaklığı: {(colors.wheelGlowOpacity * 100).toFixed(0)}%</label>
          <div className="slider-container">
            <input type="range" min="0" max="100"
              value={colors.wheelGlowOpacity * 100}
              onChange={(e) => onColorChange('wheelGlowOpacity', parseFloat((e.target.value / 100).toFixed(2)))}
              className="opacity-slider"
            />
            <span className="color-value">{(colors.wheelGlowOpacity * 100).toFixed(0)}%</span>
          </div>
        </div>
      </div>

      <div className="editor-section">
        <h4>Dekorasyon Renkleri</h4>
        {row("Halka / Ampul:", "ringAccentColor")}
        {row("Yonca Rengi:", "cloverColor")}
        {row("Işıma Rengi:", "ambientGlowColor")}
        {row("Sehpa Vurgu:", "standAccentColor")}
      </div>
    </div>
  );
};

export default WheelEditor;
